import React from 'react';
import { Autocomplete, TextField } from '@mui/material';
import './SearchableSelect.scss';

/**
 * SearchableSelect
 *
 * A controlled, type-to-search select built on MUI's `Autocomplete`.
 *
 * This is intentionally NOT tied to react-hook-form (unlike `FormAutoComplete`
 * in common-client2). It is a plain controlled component meant to replace
 * `StyledSelect` where the type-to-search behaviour of the old react-select
 * based dropdowns needs to be restored, while keeping the Material UI look.
 *
 * The `value` and the `onChange` argument are option OBJECTS (or `null`), so a
 * legitimate "None" option (whose `value` may be `null`) can be distinguished
 * from "nothing selected".
 *
 * @param {object} props
 * @param {Array<object>} props.options              Option list.
 * @param {object|null} [props.value]                Currently selected option object (or null).
 * @param {(option: object|null) => void} props.onChange  Called with the selected option (or null when cleared).
 * @param {string} [props.placeholder]               Placeholder shown when nothing is selected.
 * @param {boolean} [props.disabled]
 * @param {boolean} [props.error]
 * @param {(option: object) => string} [props.getOptionLabel]        Defaults to option.label ?? option.name.
 * @param {(option: object, value: object) => boolean} [props.isOptionEqualToValue]  Defaults to comparing `.value`.
 */
const SearchableSelect = ({
    options = [],
    value = null,
    onChange,
    placeholder,
    disabled = false,
    error = false,
    getOptionLabel = (option) => (option?.label ?? option?.name ?? ''),
    isOptionEqualToValue = (option, selected) => option?.value === selected?.value,
    ...props
}) => (
    <Autocomplete
        className="searchable-select"
        fullWidth
        disableClearable
        disabled={disabled}
        options={options}
        value={value ?? null}
        onChange={(event, option) => {
            if (onChange) {
                onChange(option ?? null);
            }
        }}
        getOptionLabel={getOptionLabel}
        isOptionEqualToValue={isOptionEqualToValue}
        renderInput={(params) => (
            <TextField
                {...params}
                variant="outlined"
                placeholder={placeholder}
                error={error}
            />
        )}
        {...props}
    />
);

export default SearchableSelect;
